/**
 * A request a session's runtime answered with an error status. Every runtime
 * rejects with it, so its callers read the status the same way: below 500 the
 * runtime refused the request, and any other rejection leaves its outcome
 * unknown.
 */
export class CodeSessionRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * Whether the runtime refused a request that failed with `error`: it answered
 * below 500. Any other failure, a 5xx or no answer at all, leaves the
 * request's outcome unknown.
 */
export function isRefusal(error: unknown): error is CodeSessionRequestError {
  return error instanceof CodeSessionRequestError && error.status < 500;
}

/**
 * What a failed call means for the person: `refused`, the runtime's own answer, which a Retry
 * would not change; `gone`, the session no longer exists there; `elsewhere`, nothing here could
 * run the session, so its page says where it runs; `unconfirmed`, the runtime took the call and
 * never finished answering it, so it may have done its work; `lost`, any other failure, which a
 * Retry may mend.
 */
export type SessionFailureKind = 'refused' | 'gone' | 'elsewhere' | 'unconfirmed' | 'lost';

/**
 * A failed call its runtime worded for the person, which a page shows as it is. `status` stays the
 * request's answer for the reads that retry by it (`isRefusal`), so a 401 is a refusal there while
 * its `kind`, `lost`, says a Retry may mend it.
 */
export class SessionFailure extends CodeSessionRequestError {
  constructor(
    message: string,
    status: number,
    readonly kind: SessionFailureKind,
  ) {
    super(message, status);
  }

  get retryable(): boolean {
    return this.kind === 'lost' || this.kind === 'unconfirmed';
  }
}

/**
 * A send the runtime may have taken: its row stays, rather than its text coming back to be sent
 * twice, and the first read begun after the send gave up settles it (`UnconfirmedSends`): listed,
 * the runtime got it; not listed, the read carries it as a failed row with Retry.
 */
export class UnconfirmedPromptError extends Error {
  constructor() {
    super('Could not confirm whether the message was sent');
  }
}

/** How long a toast with Retry stays: the toaster's 4 s suits a notice, not a button to press. */
export const RETRY_TOAST_MS = 10_000;

/**
 * What the toast of a failed action adds for its Retry, which `retry` runs: the button and the time
 * to press it, for a failure its runtime says a Retry can mend, or nothing.
 */
export function retryToast(error: unknown, retry: () => void) {
  return error instanceof SessionFailure && error.retryable
    ? { action: { label: 'Retry', onClick: retry }, duration: RETRY_TOAST_MS }
    : {};
}
